import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HubIcon from '@mui/icons-material/Hub';
import VerifiedIcon from '@mui/icons-material/Verified';

const TEAL = '#0d9488';
const DARK_BG = '#052d50';
const GRADIENT = 'linear-gradient(135deg, #052d50 0%, #478289 100%)';

const pillars = [
  { icon: <HandshakeIcon sx={{ fontSize: 44 }} />, title: 'Partnership', desc: 'We are more than investors — we are partners. We work alongside founders, offering guidance, support, and a shared commitment to success.' },
  { icon: <AttachMoneyIcon sx={{ fontSize: 44 }} />, title: 'Fundraising', desc: 'Leverage iAngels\' network of 20,000+ accredited investors and institutional partners to accelerate your fundraising process.' },
  { icon: <HubIcon sx={{ fontSize: 44 }} />, title: 'Network', desc: 'Tap into a global network of industry leaders, mentors, and fellow entrepreneurs. Connections that open doors and create opportunities.' },
  { icon: <VerifiedIcon sx={{ fontSize: 44 }} />, title: 'Professionalism', desc: 'From term sheets to board representation, we bring the highest standards of professionalism to every interaction.' },
];

const Entrepreneurs = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Helmet>
        <title>Entrepreneurs — iAngels | Partner with Us</title>
        <meta name="description" content="iAngels for Entrepreneurs. We are builders like you. Partner with iAngels for fundraising, network access, and professional support." />
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: GRADIENT, color: 'white', py: { xs: 8, md: 14 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            iAngels for Entrepreneurs
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 300, maxWidth: 600, mx: 'auto' }}>
            We are builders like you. We understand the challenges of building a company from the ground up —
            and we're here to help you every step of the way.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ bgcolor: TEAL, px: 5, py: 1.5, '&:hover': { bgcolor: '#0b7d73' } }}
          >
            Apply Now
          </Button>
        </Container>
      </Box>

      {/* Four Pillars */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 1, color: DARK_BG }}>
            Why Partner with iAngels?
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5, maxWidth: 500, mx: 'auto' }}>
            Four pillars that define how we work with founders.
          </Typography>
          <Grid container spacing={4}>
            {pillars.map((p) => (
              <Grid item xs={12} sm={6} md={3} key={p.title}>
                <Card elevation={0} sx={{ height: '100%', textAlign: 'center', p: 3, border: '1px solid #eee', borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ color: TEAL, mb: 2 }}>{p.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: DARK_BG }}>{p.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{p.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Apply CTA */}
      <Box sx={{ py: 8, background: GRADIENT, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
            Ready to Build Together?
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 4 }}>
            Submit your application and let's explore how iAngels can help your startup grow.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ bgcolor: 'white', color: DARK_BG, px: 5, py: 1.5, fontWeight: 700, '&:hover': { bgcolor: '#e0e0e0' } }}
          >
            Apply Here
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Entrepreneurs;
